// DEV CURRENT

import {
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
} from '@chakra-ui/accordion'
import {
  FormControl,
  FormHelperText,
  FormLabel,
  FormErrorMessage,
} from '@chakra-ui/form-control'
import { Box, Flex } from '@chakra-ui/layout'
import { HStack, Input, Switch, Text as ChakraText } from '@chakra-ui/react'
import type { FormikProps } from 'formik'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { MdDevicesOther } from 'react-icons/md'
import { colors } from '../../../../ui-kit'
import { SettingsToggleBox } from '..'
import type { SettingsResponse } from '../../../interfaces'
import PermissionGuard from '../../PermissionGuard'
import { PermissionsEnum } from '../../../../utils'

const ImageSettingsDeviceBased = ({
  values,
  handleChange,
  errors,
  touched,
  handleBlur,
}: FormikProps<SettingsResponse>): React.ReactElement => {
  const { t } = useTranslation()
  return (
    <PermissionGuard
      permission={PermissionsEnum.SELF_IMAGE_SETTINGS_MANAGE}
    >
      {
        (hasPermission: boolean) => (
          <AccordionItem>
            <AccordionButton px={12} py={8}>
              <Flex
                flex="1"
                textAlign="left"
                lineHeight="1.5em"
                fontWeight="700"
                style={{ gap: '1em' }}
              >
                <MdDevicesOther fontSize="1.5em" />
                {t('device_based', 'Device Based')}
              </Flex>
              <Box
                background={colors.appPaleGreyThree}
                borderRadius="50%"
                padding="0.25em"
              >
                <AccordionIcon color={colors.appSlate} display="block" />
              </Box>
            </AccordionButton>

            <AccordionPanel px={12} py={8}>
              <SettingsToggleBox>
                <Flex justifyContent="space-between" width="100%">
                  <ChakraText fontWeight="700">
                    {t(
                      'device_based_restrict_image_size',
                      "Restrict image size based on user's device",
                    )}
                  </ChakraText>
                  <Switch
                    name="device.enabled"
                    isChecked={values.device.enabled}
                    onChange={handleChange}
                    isDisabled={!hasPermission}
                  />
                </Flex>
                <ChakraText>{t('device_based_description')}</ChakraText>
                <ChakraText>
                  {t('we_recommend')} :&nbsp;
                  <ChakraText as="span" color="appGreenishTeal">
                    ON
                  </ChakraText>
                  {t(
                    'device_based_recommendation',
                    ', if you are using the same image urls on desktop and mobile',
                  )}
                </ChakraText>

                <FormLabel>{t('desktop', 'Desktop')}</FormLabel>
                <HStack spacing="1.5em" width="100%" alignItems="flex-start">
                  <FormControl
                    isInvalid={
                      touched.device?.desktop?.width &&
                      Boolean(errors.device?.desktop?.width)
                    }
                    isRequired
                  >
                    <Flex flexDir="column">
                      <Input
                        type="number"
                        name="device.desktop.width"
                        value={values.device.desktop.width}
                        onChange={handleChange}
                        background={colors.appWhite}
                        disabled={!hasPermission || !values.device.enabled}
                        onBlur={handleBlur}
                      />
                      <FormHelperText fontStyle="bold">
                        {t('width', 'Width')}
                      </FormHelperText>
                      <Flex minH={8}>
                        <FormErrorMessage>
                          {errors.device?.desktop?.width}
                        </FormErrorMessage>
                      </Flex>
                    </Flex>
                  </FormControl>
                  <FormControl
                    isInvalid={
                      touched.device?.desktop?.height &&
                      Boolean(errors.device?.desktop?.height)
                    }
                    isRequired
                  >
                    <Flex flexDir="column">
                      <Input
                        type="number"
                        name="device.desktop.height"
                        value={values.device.desktop.height}
                        onChange={handleChange}
                        background={colors.appWhite}
                        disabled={!hasPermission || !values.device.enabled}
                        onBlur={handleBlur}
                      />
                      <FormHelperText fontStyle="bold">
                        {t('height', 'Height')}
                      </FormHelperText>
                      <Flex minH={8}>
                        <FormErrorMessage>
                          {errors.device?.desktop?.height}
                        </FormErrorMessage>
                      </Flex>
                    </Flex>
                  </FormControl>
                </HStack>

                <FormLabel>{t('mobile', 'Mobile')}</FormLabel>
                <HStack spacing="1.5em" width="100%" alignItems="flex-start">
                  <FormControl
                    isInvalid={
                      touched.device?.mobile?.width &&
                      Boolean(errors.device?.mobile?.width)
                    }
                    isRequired
                  >
                    <Flex flexDir="column">
                      <Input
                        type="number"
                        name="device.mobile.width"
                        value={values.device.mobile.width}
                        onChange={handleChange}
                        background={colors.appWhite}
                        disabled={!hasPermission || !values.device.enabled}
                        onBlur={handleBlur}
                      />
                      <FormHelperText fontStyle="bold">
                        {t('width', 'Width')}
                      </FormHelperText>
                      <Flex minH={8}>
                        <FormErrorMessage>
                          {errors.device?.mobile?.width}
                        </FormErrorMessage>
                      </Flex>
                    </Flex>
                  </FormControl>
                  <FormControl
                    isInvalid={
                      touched.device?.mobile?.height &&
                      Boolean(errors.device?.mobile?.height)
                    }
                    isRequired
                  >
                    <Flex flexDir="column">
                      <Input
                        type="number"
                        name="device.mobile.height"
                        value={values.device.mobile.height}
                        onChange={handleChange}
                        background={colors.appWhite}
                        disabled={!hasPermission || !values.device.enabled}
                        onBlur={handleBlur}
                      />
                      <FormHelperText fontStyle="bold">
                        {t('height', 'Height')}
                      </FormHelperText>
                      <Flex minH={8}>
                        <FormErrorMessage>
                          {errors.device?.mobile?.height}
                        </FormErrorMessage>
                      </Flex>
                    </Flex>
                  </FormControl>
                </HStack>
              </SettingsToggleBox>
            </AccordionPanel>
          </AccordionItem>
        )
      }
    </PermissionGuard>
  )
}

export default ImageSettingsDeviceBased
